/**
 * Date calculation utilities for travel forms
 */

/**
 * Calculate the number of days between two dates (inclusive)
 * Formula: (expiryDate - effectiveDate) + 1
 * 
 * @param effectiveDate - Start date in YYYY-MM-DD format
 * @param expiryDate - End date in YYYY-MM-DD format
 * @returns Number of days or null if dates are invalid
 */
export const calculateDaysBetween = (
  effectiveDate: string, 
  expiryDate: string
): number | null => {
  // Check if both dates are provided
  if (!effectiveDate || !expiryDate) {
    return null;
  }

  // Parse dates
  const startDate = new Date(effectiveDate);
  const endDate = new Date(expiryDate);
  
  // Validate dates
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }

  // Check if expiry date is after effective date
  if (endDate < startDate) {
    return null;
  }
  
  // Calculate difference in milliseconds
  const timeDiff = endDate.getTime() - startDate.getTime();
  
  // Convert to days and add 1 (inclusive calculation)
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
  
  return daysDiff;
};

/**
 * Format days count for display
 * 
 * @param days - Number of days
 * @returns Formatted string for display
 */
export const formatDaysDisplay = (days: number | null): string => {
  if (days === null || days <= 0) {
    return '';
  }
  
  return `${days} Day${days === 1 ? '' : 's'}`;
};

/**
 * Validate if a date string is in valid format and not in the past
 * 
 * @param dateString - Date string to validate
 * @param allowPastDates - Whether to allow past dates (default: false)
 * @returns Boolean indicating if date is valid
 */
export const isValidTravelDate = (
  dateString: string, 
  allowPastDates: boolean = false
): boolean => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return false;
  
  // Check if date is not in the past (unless allowed)
  if (!allowPastDates) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    if (date < today) return false;
  }
  
  return true;
};

/**
 * Calculate maximum expiry date (180 days from effective date)
 *
 * @param effectiveDate - Start date in YYYY-MM-DD format
 * @returns Maximum expiry date string or null if invalid
 */
export const calculateMaxExpiryDate = (effectiveDate: string): string | null => {
  if (!effectiveDate) return null;
  
  const effective = new Date(effectiveDate);
  if (isNaN(effective.getTime())) return null;
  
  // Add 180 days
  const maxExpiry = new Date(effective);
  maxExpiry.setDate(maxExpiry.getDate() + 179);
  
  // Use local date components instead of UTC to avoid timezone shifts
  const year = maxExpiry.getFullYear();
  const month = (maxExpiry.getMonth() + 1).toString().padStart(2, '0');
  const day = maxExpiry.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Check if expiry date is within 180-day limit
 *
 * @param effectiveDate - Start date in YYYY-MM-DD format
 * @param expiryDate - End date in YYYY-MM-DD format
 * @returns Boolean indicating if expiry date is within limit
 */
export const isWithin180DayLimit = (effectiveDate: string, expiryDate: string): boolean => {
  const maxDate = calculateMaxExpiryDate(effectiveDate);
  if (!maxDate || !expiryDate) return false;
  
  return new Date(expiryDate) <= new Date(maxDate);
};

/**
 * Format date for display in user-friendly format
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Calculate maximum coverage date for Annual plans (90 days from effective date)
 *
 * @param effectiveDate - Start date in YYYY-MM-DD format
 * @returns Maximum coverage date string or null if invalid
 */
export const calculateAnnualMaxCoverageDate = (effectiveDate: string): string | null => {
  if (!effectiveDate) return null;
  
  const effective = new Date(effectiveDate);
  if (isNaN(effective.getTime())) return null;
  
  // Add 90 days (89 days + 1 for inclusive calculation)
  const maxCoverage = new Date(effective);
  maxCoverage.setDate(maxCoverage.getDate() + 89);
  
  // Use local date components instead of UTC to avoid timezone shifts
  const year = maxCoverage.getFullYear();
  const month = (maxCoverage.getMonth() + 1).toString().padStart(2, '0');
  const day = maxCoverage.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};